import { contractRequest } from "@/portals/clinician/api/transport";
import type { ReviewTriggerType } from "@/features/patient/records/api/dataPipelineApi";
import type { QueueItemPreview } from "@/portals/clinician/api/reviewQueueApi";

// Module 6 — admin oversight of the shared review queue. Admin token; failures go
// through the one contract error parser (contractErrors.ts).

export interface ReviewQueueSummary {
  /** AWAITING_CLINICIAN with no active holder. */
  unclaimedCount: number;
  /** Of those, waiting longer than UNCLAIMED_ALERT_HOURS. */
  overdueCount: number;
  oldestWaitingSince: string | null;
}

export interface AdminQueueItem {
  id: string;
  triggerType: ReviewTriggerType;
  medicineName: string;
  patientDisplayName: string;
  waitingSince: string;
  overdue: boolean;
}

/** Read-only look at a queued item before assigning it — the clinician
 *  preview plus the patient (admins already see the name in the list). */
export interface AdminQueueItemDetail extends QueueItemPreview {
  patient: { displayName: string; dateOfBirth: string | null };
}

export interface AssignResult {
  id: string;
  itemStatus: "IN_CLINICIAN_REVIEW";
  clinician: { userId: string; firstName: string; lastName: string };
}

export const getReviewQueueSummaryApi = () =>
  contractRequest<ReviewQueueSummary>("/api/v1/admin/review-queue/summary", { method: "GET", portal: "admin" });

/** Oldest first. */
export const listAdminReviewQueueApi = () =>
  contractRequest<{ items: AdminQueueItem[] }>("/api/v1/admin/review-queue", { method: "GET", portal: "admin" });

/** Read-only, changes nothing. 200 · 409 ITEM_NOT_ASSIGNABLE (no longer queued) · 404 REVIEW_ITEM_NOT_FOUND */
export const getAdminQueueItemApi = (id: string) =>
  contractRequest<AdminQueueItemDetail>(`/api/v1/admin/review-queue/${encodeURIComponent(id)}`, { method: "GET", portal: "admin" });

/** 404 REVIEW_ITEM_NOT_FOUND / CLINICIAN_NOT_FOUND · 409 ITEM_NOT_ASSIGNABLE / CLINICIAN_NOT_ELIGIBLE */
export const assignReviewItemApi = (id: string, clinicianUserId: string) =>
  contractRequest<AssignResult>(`/api/v1/admin/review-items/${encodeURIComponent(id)}/assign`, {
    method: "POST",
    portal: "admin",
    body: { clinicianUserId },
  });
